import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  isDevMode,
  inject,
  input,
  computed,
  effect,
  output,
  viewChild,
} from '@angular/core';

import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { ButtonMode, ButtonSize, ButtonTheme, ButtonType } from './enums';
import { IconPosition } from '../../icons/enums';
import { ButtonIcon } from './interfaces';
import { LoaderTheme } from '../../loader/enums';
import { AriaOptions } from '../../interfaces';

@Component({
  selector: 'fcom-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss', '../buttons-shared-styles.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.fill-mode]': 'fillMode()',
    '[class.fill-mode-mobile]': 'fillModeMobile()',
  },
})
export class ButtonComponent {
  private logger = inject(MonitoringLogger, { optional: true });
  private themeService = inject(FinnairThemeService);

  /**
   * The size of the button, currently 2 static sizes
   */
  size = input(ButtonSize.MEDIUM);

  /**
   * There are 8 themes available right now that renders different backgrounds, font colors and borders
   */
  theme = input(ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE);

  computedTheme = computed((): ButtonTheme => {
    const siteTheme: FinnairTheme = this.themeService.siteTheme();

    if (siteTheme === FinnairTheme.LEGACY) {
      return this.theme();
    }

    switch (this.theme()) {
      case ButtonTheme.LIGHT_PRIMARY_CTA_HEATHER:
      case ButtonTheme.DARK_PRIMARY_CTA_HEATHER:
      case ButtonTheme.PRIMARY_ACCENT:
        return ButtonTheme.PRIMARY_ACCENT;
      case ButtonTheme.DARK_SECONDARY_OUTLINE:
      case ButtonTheme.SECONDARY_OUTLINE_REVERSED:
        return ButtonTheme.SECONDARY_OUTLINE_REVERSED;
      case ButtonTheme.LIGHT_TERTIARY_GHOST:
      case ButtonTheme.TERTIARY_GHOST:
        return ButtonTheme.TERTIARY_GHOST;
      case ButtonTheme.DARK_TERTIARY_GHOST:
      case ButtonTheme.TERTIARY_GHOST_REVERSED:
        return ButtonTheme.TERTIARY_GHOST_REVERSED;
      default:
        return ButtonTheme.SECONDARY_OUTLINE;
    }
  });

  /**
   * The pre-translated text to show
   */
  text = input.required<string>();

  /**
   * The aria tags that must be added to a button or link if it has a loading or disabled state
   */
  aria = input<AriaOptions>({});

  /**
   * An icon to include, with its category, name and position
   */
  icon = input<ButtonIcon>();

  /**
   * Whether the button should be disabled or not
   */
  disabled = input(false);

  /**
   * Disables the button and either replaces the current icon with the loading spinner. Or will add an icon to the right of text.
   */
  loading = input(false);

  /**
   * The type of button, for example "button" or "submit"
   */
  type = input(ButtonType.BUTTON);

  /**
   * The pre-translated hover text to display
   */
  title = input<string>();

  /**
   * If button should render as an a tag, then pass in the url
   */
  href = input<string | undefined>();

  /**
   * If link, should it open in a new tab
   */
  openInNewTab = input<boolean | undefined>();

  /**
   * If link, is it external
   */
  externalLink = input<boolean | undefined>();

  /**
   * Do we fill the whole container or work as inline-block
   */
  mode = input(ButtonMode.INLINE);

  /**
   * Can the button be focused by tab key
   */
  removeFromNavigation = input(false);

  /**
   * Focus to button after some action for accessibility
   */
  focusOnRender = input<boolean>(false);

  id = input<string>();

  /**
   * Only applicable when using the href input. With this you can control the `rel` parameter on the a tag.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
   */
  rel = input<string>();

  buttonElement = viewChild<ElementRef>('buttonElement');

  /**
   * Event emitter for blur event
   */
  blurBtn = output<void>();
  clickBtn = output<Event>();

  public readonly IconPosition = IconPosition;
  public readonly ButtonSize = ButtonSize;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonMode = ButtonMode;
  public readonly LoaderTheme = LoaderTheme;

  fillMode = computed(() => {
    return this.mode() === ButtonMode.FILL;
  });
  fillModeMobile = computed(() => {
    return this.mode() === ButtonMode.FILL_MOBILE;
  });
  ariaLabel = computed(() => {
    if (this.loading()) {
      return this.aria().loadingLabel || null;
    }
    return this.aria().label || null;
  });
  ariaRole = computed(() => {
    return (!this.href() && this.aria()?.role !== 'button') || Boolean(this.href()) ? this.aria()?.role : undefined;
  });

  constructor() {
    effect(() => {
      if (this.loading() && isDevMode() && !this.aria().loadingLabel) {
        this.logger?.warn('Missing aria label for loading button');
      }
    });
    effect(() => {
      if (this.focusOnRender()) {
        this.focus();
      }
    });
  }

  onClick(event: Event): void {
    if (event) {
      if (this.disabled() || this.loading() || this.aria()?.disabled) {
        event.preventDefault();
        return;
      }

      this.clickBtn.emit(event);
    }
  }

  focus(): void {
    this.buttonElement()?.nativeElement?.focus();
  }
}
